import { z } from 'zod';
import { RequestBoxSchema } from '../request-boxes.js';
import { StorageObjectKeySchema } from '../storage.js';
import { AuditEntrySchema, ChatMessageSchema } from './actors-chat-audit.js';
import { RequestCarrierSchema, type RequestCarrier } from './carriers.js';
import { RequestDestinationReadSchema } from './destinations.js';
import {
  RequestPackagingOptionProjectionSchema,
  RequestRoadOptionProjectionSchema,
} from './dictionary-selections.js';
import { DraftRequestItemSchema, type DraftRequestItem } from './items.js';
import { RequestKindSchema, isContainerKind, isSimpleKind } from './kind.js';
import {
  CarrierBidCurrencySchema,
  isDraftLikeRequestStatus,
  RequestRefusalReasonSchema,
  RequestStatus,
} from './status.js';

// WHY: a relaxed top-level shape (no `.min(1)` on `name`/route fields,
// items use `DraftRequestItemSchema` so `quantity` can be `0` and `name`
// can be empty) is the structural floor that rounds draft-like rows
// (`DRAFT` and `RETURN`) safely through wire/list/detail responses.
// Active-row strictness — non-empty `name`/route/items[] text and
// `quantity >= 1` — is re-asserted at parse time by
// `enforceActiveStrictness` so the only loosening that lands on
// non-draft-like rows is the empty-strings and `quantity=0` window the
// editor needs.
//
// INVARIANT: keys, key order, nullability, and nested schemas
// (`RequestRouteSchema` strictness, `StorageObjectKeySchema` non-empty
// attachments, server-minted `winner` regex) match the pre-DRAFT
// `RequestSchema` exactly. The only differences are `name`/route field
// `.min(1)` removed and `items: DraftRequestItemSchema[]` (which still
// rejects unknown keys via its own `.strict()`). Stale-bundle clients
// continue to reject extra top-level keys via `.strict()`.
export const RequestObjectShape = {
  id: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
  name: z.string(),
  status: z.nativeEnum(RequestStatus),
  // WHY (#701): discriminator that switches the wire-level invariants on
  //   `transportTypeId`, `items[].weight`, `items[].volume`, and `boxes`.
  //   Default `STANDARD` on read (backed by the Prisma column default) so
  //   pre-#701 rows round-trip without backfill.
  kind: RequestKindSchema,
  // WHY (#701): nullable on read because STANDARD rows MUST carry null
  //   here. The CONTAINER-required invariant lives at the input-refine
  //   layer and the service.
  transportTypeId: z.string().uuid().nullable(),
  manager: z.string().min(1),
  logist: z.string().nullable(),
  // INVARIANT: stable user IDs for party-aware client gating
  //   (`apps/web/src/features/requests/role.ts`). The display-name
  //   fields above (`manager`, `logist`) cannot be matched against
  //   `User.id` from the auth store because the wire carries names,
  //   not IDs. With Phase 2 of `requests.scope.all` (logist_cn sees
  //   ALL rows), the client must distinguish "this row is mine to
  //   act on" from "this row is visible but I'm not a party" — the
  //   backend chokepoint already enforces ownership, but rendering a
  //   transition button for a row the actor cannot complete would
  //   yield a confusing 403 round-trip. These IDs let the UI
  //   collapse the action set client-side. They round-trip
  //   read-only — every write surface (CREATE, PATCH header,
  //   manager/logist reassignment) keeps the manager/logist as
  //   server-derived. Schema is `.strict()`, so a stale-bundle
  //   client running against a new backend that sends these fields
  //   would reject the response — see expand-and-contract note in
  //   the PR description.
  managerId: z.string().uuid(),
  logistId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  slaDeadline: z.string().datetime().nullable(),
  inWorkAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  // WHY (#960): logistics-completion timestamp for the CURRENT cycle.
  //   Stamped by the backend on `IN_WORK -> PROCESSED` (in #961, out of
  //   scope here); read-only and server-derived on the wire. The web
  //   layer (#962) reads `processedAt ?? closedAt` to render historical
  //   SLA duration on PROCESSED/SUCCESS/REFUSED/CLOSED/ARCHIVE rows.
  //   Nullable because pre-#960 rows and pre-PROCESSED rows have no
  //   value; the #960 backfill derives it from the `TRANSITION_PROCESS`
  //   audit row when available, falls back to `closedAt` for legacy
  //   terminal rows without a process audit, and leaves unbackfillable
  //   `PROCESSED` rows as null. Schema is `.strict()`, so a stale-bundle
  //   client running against a new backend that emits this field would
  //   reject the response — deploy order: #960 ships to web before #961
  //   stamps the column on backend.
  processedAt: z.string().datetime().nullable(),
  // WHY (#700): `route.from` is server-set to the literal `'Китай'`
  //   (UI does not surface it) and `route.to` is a denormalised mirror
  //   of `destinations[].arrivalCity.name` kept on the row so the
  //   pre-existing `request_search_hash_update` trigger continues to
  //   populate `searchHash`. Clients read `destinations` for the
  //   canonical source; `route` is retained on the wire only for
  //   backward compatibility with stale bundles.
  route: z.object({ from: z.string(), to: z.string() }).strict(),
  // WHY (#700 / #1053): the multiselect of arrival cities. STANDARD
  //   rows carry `min(1)` (enforced via `enforceActiveStrictness` on
  //   non-draft-like statuses). CONTAINER rows are request-wide and may
  //   carry zero destinations; legacy CONTAINER rows persisted before
  //   #1053 carry at least one and round-trip on the read side without
  //   a service-layer migration. Structural floor is array without a
  //   length bound; kind-aware floor lives in the refine.
  destinations: z.array(RequestDestinationReadSchema),
  // WHY (#996): request-level multiselects of Roads and Packaging.
  //   Read shape includes enough data to render inactive historical
  //   selections (`active = false` chips). The arrays default to `[]`
  //   on both reads (pre-#996 rows) and never gain a `.min(N)` floor —
  //   selections are optional by design. Order is server-controlled,
  //   sorted by `(sortOrder asc, id asc)` so chip placement is stable.
  roads: z.array(RequestRoadOptionProjectionSchema).optional(),
  packaging: z.array(RequestPackagingOptionProjectionSchema).optional(),
  weight: z.number().nonnegative(),
  volume: z.number().nonnegative(),
  items: z.array(DraftRequestItemSchema),
  carriers: z.array(RequestCarrierSchema),
  // WHY (#569): winner identity moved from carrier-level (`TR-NNNN`
  //   carrier id) to bid-level (`RequestCarrierBid.id`, UUID). The
  //   winning carrier is derived client-side by finding the carrier
  //   whose `bids[]` contains this bid id; the wire stays single-source
  //   (no parallel `winnerCarrierId`).
  winnerBidId: z.string().uuid().nullable(),
  // INVARIANT: attachments are object-storage keys with the same
  // non-empty contract as RequestItem.photos so a broken `''`
  // reference cannot land in a request payload.
  attachments: z.array(StorageObjectKeySchema),
  chat: z.array(ChatMessageSchema),
  audit: z.array(AuditEntrySchema),
  // WHY (#1077): tightened from opaque `z.string().nullable()` to
  //   `uuid()` now that `Request.leadId` is a typed FK to `Lead.id`
  //   (`@db.Uuid` + ON DELETE SET NULL). The DB precheck in migration
  //   `20260523060000_add_lead_links_to_requests` proved no legacy
  //   non-UUID values exist, so tightening the wire is safe and surfaces
  //   client-side parse errors fast instead of failing at the DB.
  leadId: z.string().uuid().nullable(),
  // WHY (#1088): mirror of `leadId` for the commercial Deal link
  //   added in #1087. `Request.dealId` is `@db.Uuid` + ON DELETE SET
  //   NULL — deleting a Deal nulls this slot but preserves the request.
  //   Always nullable; rows created before the row was bound carry null.
  //   `.optional()` + `.default(null)` so pre-#1088 MSW handlers and
  //   test fixtures that don't yet emit this slot still parse cleanly;
  //   the real backend always sets it (toWire is exhaustive).
  dealId: z.string().uuid().nullable().optional().default(null),
  // WHY (#1596): opt-in funnel-link public codes — the `L-#####` / `D-#####`
  //   of the linked Lead/Deal, surfaced ONLY when the caller passes
  //   `?includeFunnelLinks=true`. Optional so default payloads (which omit
  //   the keys) stay valid under `.strict()`; nullable for unlinked rows.
  //   The backend never emits these keys unless opted in, mirroring the
  //   `boxes` rolling-deploy expand.
  leadPublicCode: z.string().nullable().optional(),
  dealPublicCode: z.string().nullable().optional(),
  returnReason: z.string().nullable(),
  // WHY (#585): structured refusal storage. Both fields are null on
  //   non-REFUSED rows. The optional comment is bounded at the wire
  //   layer (`REFUSAL_COMMENT_MAX_LENGTH`) and never duplicated into
  //   audit ledger payload (Resolved Decision § Audit).
  refusalReason: RequestRefusalReasonSchema.nullable(),
  refusalComment: z.string().nullable(),
  // WHY (#585): pre-ARCHIVE status snapshot so unarchive can restore
  //   SUCCESS/REFUSED rather than collapsing every archived row to
  //   CLOSED. Legacy archived rows carry null and unarchive to CLOSED
  //   by app-level fallback.
  archivedFromStatus: z.nativeEnum(RequestStatus).nullable(),
  // WHY (#1052): China-only cargo-insurance request. `insuranceRequested`
  //   is the manager-controlled flag; `insuranceCargoValue` +
  //   `insuranceCargoCurrency` form a both-or-neither pair that MUST be
  //   set when requested and null when not requested (enforced at the
  //   wire by `enforceInsuranceInvariants`). The carrier quote is
  //   inlined per-carrier on `RequestCarrierSchema.insuranceQuotes`.
  insuranceRequested: z.boolean(),
  insuranceCargoValue: z.number().positive().nullable(),
  insuranceCargoCurrency: CarrierBidCurrencySchema.nullable(),
  // WHY (#1053): container-only free-text delivery address. The wire
  //   floor accepts nullable string at every status (draft-friendly);
  //   `enforceActiveStrictness` adds the kind-aware invariant —
  //   CONTAINER rows require a non-empty value, STANDARD rows MUST be
  //   null. PII-grade: the backend mirrors it into `routeTo` for
  //   CONTAINER on every write so the existing search-hash trigger
  //   continues to index the value; audit/log redaction live next to
  //   the redaction list (`apps/backend/src/shared/logging/redact-paths.ts`).
  deliveryAddress: z.string().nullable(),
  // WHY (#1392, #2584): STANDARD is null-only; SIMPLE and CONTAINER may
  //   carry a value. The wire floor accepts nullable string at EVERY status
  //   and kind — intentionally
  //   NOT enforced in `enforceActiveStrictness`. Requiredness lives only
  //   on the write/input paths (create + draft-update). Reason (LOCKED):
  //   pre-existing ACTIVE CONTAINER rows carry `pickupAddress = null`; a
  //   read-side requiredness refine would reject them and break rolling
  //   deploys. Unlike `deliveryAddress`, it is never mirrored into
  //   `routeTo`; PII-grade redaction lives next to the redaction list
  //   (`apps/backend/src/shared/logging/redact-paths.ts`).
  pickupAddress: z.string().nullable(),
  // WHY (#599, rolling-deploy): boxes are an optional expand. Legacy
  //   bundles that never project the key parse responses that omit
  //   `boxes` (the list path always omits per Decision #27 of #599);
  //   new bundles parse responses from a not-yet-upgraded backend
  //   without rejecting. Backend emits `boxes` only on detail/mutation
  //   paths that opted in (`?includeBoxes=true` query flag, or a
  //   draft-save body that explicitly includes `boxes`).
  boxes: z.array(RequestBoxSchema).optional(),
} as const;

export const RequestObjectBase = z.object(RequestObjectShape).strict();

// WHY: the active-row contract that draft-like statuses relax — non-empty
// header `name` + `route.{from,to}`, non-empty item `name`, item
// `quantity >= 1`. Re-asserted via `superRefine` so the relaxed structural
// shape stays a single source of truth and draft-like round-tripping does
// not drift item strictness for the rest of the state machine. Issue codes
// match the shapes Zod would have produced under the pre-#521 `.min(1)`
// constraints so existing consumers that switch on issue `code` keep
// working.
export const enforceActiveStrictness = (
  req: {
    status: RequestStatus;
    kind: z.infer<typeof RequestKindSchema>;
    transportTypeId: string | null;
    name: string;
    route: { from: string; to: string };
    items: ReadonlyArray<DraftRequestItem>;
    insuranceRequested: boolean;
    insuranceCargoValue: number | null;
    insuranceCargoCurrency: string | null;
    deliveryAddress: string | null;
    // WHY (#1392): read here only for the status-agnostic non-container-null
    //   pairing; no active-CONTAINER requiredness (see refine body).
    pickupAddress: string | null;
    carriers: ReadonlyArray<RequestCarrier>;
    destinations: ReadonlyArray<{ arrivalCityId: string }>;
    // WHY (#1303): SIMPLE forbids boxes, so the read-model refine needs
    //   the boxes array to reject a SIMPLE row that carries any. Optional
    //   on the wire (list paths omit `boxes`), so an omitted value is the
    //   no-boxes case.
    boxes?: ReadonlyArray<unknown> | undefined;
  },
  ctx: z.RefinementCtx,
): void => {
  // WHY (#1052): insurance fields are a status-agnostic wire invariant.
  //   When `insuranceRequested = true`, cargo value AND currency must
  //   both be non-null. When `false`, both MUST be null — the server
  //   canonicalises that on the write path, so a non-null leak would
  //   indicate a stale projector.
  if (req.insuranceRequested) {
    if (req.insuranceCargoValue === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['insuranceCargoValue'],
        message: 'required when insuranceRequested is true',
      });
    }
    if (req.insuranceCargoCurrency === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['insuranceCargoCurrency'],
        message: 'required when insuranceRequested is true',
      });
    }
  } else {
    if (req.insuranceCargoValue !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['insuranceCargoValue'],
        message: 'must be null when insuranceRequested is false',
      });
    }
    if (req.insuranceCargoCurrency !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['insuranceCargoCurrency'],
        message: 'must be null when insuranceRequested is false',
      });
    }
  }
  // WHY (#701 / #1053): the kind / transportTypeId / deliveryAddress
  //   pairing is a status-agnostic wire invariant — a row must satisfy
  //   it on every read regardless of draft-like vs. active state — so
  //   check it before the early-return. CONTAINER rows require a
  //   non-empty `deliveryAddress`; STANDARD rows MUST have null. Empty
  //   strings on CONTAINER are rejected as «not delivered» — the wire
  //   floor accepts `null`, but a row that landed `''` would imply the
  //   service forgot to canonicalise. The active-row strictness for
  //   `deliveryAddress.length > 0` is a separate later block so a
  //   draft-like row may still carry the empty default.
  if (isContainerKind(req.kind)) {
    if (req.transportTypeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request requires transportTypeId',
        path: ['transportTypeId'],
      });
    }
  } else {
    if (req.transportTypeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null transportTypeId',
        path: ['transportTypeId'],
      });
    }
    if (req.deliveryAddress !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null deliveryAddress',
        path: ['deliveryAddress'],
      });
    }
    // WHY (#1392, #2580, #2584): STANDARD remains null-only on reads while
    //   SIMPLE and CONTAINER may carry a pickupAddress.
    //   Active-CONTAINER pickup requiredness is deliberately NOT added here
    //   (it lives on the create/send/activate input paths) so legacy active
    //   CONTAINER rows with pickupAddress = null keep parsing.
    if (!isSimpleKind(req.kind) && req.pickupAddress !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null pickupAddress',
        path: ['pickupAddress'],
      });
    }
  }
  // WHY (#1303): a SIMPLE row is STANDARD-shaped (transportTypeId /
  //   deliveryAddress null are already enforced by the non-container
  //   branch above; the both-or-neither insurance block above already
  //   forces value/currency null when not requested) but additionally
  //   forbids insurance and boxes outright. These are status-agnostic
  //   structural invariants — a persisted SIMPLE row never carries either,
  //   so reject before the draft-like early-return.
  if (isSimpleKind(req.kind)) {
    if (req.insuranceRequested) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'simple request forbids insurance',
        path: ['insuranceRequested'],
      });
    }
    if (req.boxes !== undefined && req.boxes.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'simple request forbids boxes',
        path: ['boxes'],
      });
    }
  }
  // WHY (#1053): RequestCarrier scope pairing. STANDARD parents require
  //   every carrier row to carry a non-null arrivalCityId; CONTAINER
  //   parents require every carrier row to carry NULL. This is a
  //   structural wire invariant — applies on any status, including
  //   draft-like reads, because the row shape is identical to its
  //   persisted form.
  for (let i = 0; i < req.carriers.length; i += 1) {
    const carrier = req.carriers[i];
    if (!carrier) continue;
    if (isContainerKind(req.kind)) {
      if (carrier.arrivalCityId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'container carrier must have null arrivalCityId',
          path: ['carriers', i, 'arrivalCityId'],
        });
      }
    } else if (carrier.arrivalCityId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard carrier requires arrivalCityId',
        path: ['carriers', i, 'arrivalCityId'],
      });
    }
  }
  if (isDraftLikeRequestStatus(req.status)) return;
  // WHY (#1053): STANDARD active rows MUST carry at least one
  //   destination. CONTAINER rows are request-wide — the wire's
  //   structural array allows zero (new shape) or one+ (legacy
  //   backward-compat reads). Draft-like statuses skip this so a
  //   fresh STANDARD draft may persist with zero destinations until
  //   the manager picks at least one.
  if (!isContainerKind(req.kind) && req.destinations.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'array',
      inclusive: true,
      path: ['destinations'],
      message: 'standard request requires at least one destination',
    });
  }
  // WHY (#1053): an active CONTAINER row requires a non-empty
  //   deliveryAddress. Draft-like rows skip this — a fresh CONTAINER
  //   draft may persist `null` deliveryAddress until the manager fills
  //   the field.
  if (isContainerKind(req.kind)) {
    if (req.deliveryAddress === null || req.deliveryAddress.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request requires deliveryAddress',
        path: ['deliveryAddress'],
      });
    }
  }
  // WHY (#701, narrowed by #1447): on an active SIMPLE row every item MUST
  //   carry non-null weight/volume. STANDARD moved to a request-level total
  //   (#1447) so its per-item weight/volume are optional like CONTAINER's —
  //   the send gate enforces the request-level total instead.
  if (isSimpleKind(req.kind)) {
    for (let i = 0; i < req.items.length; i += 1) {
      const it = req.items[i];
      if (!it) continue;
      if (it.weight === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'standard request item requires weight',
          path: ['items', i, 'weight'],
        });
      }
      if (it.volume === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'standard request item requires volume',
          path: ['items', i, 'volume'],
        });
      }
    }
  }
  if (req.name.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['name'],
      message: 'String must contain at least 1 character(s)',
    });
  }
  if (req.route.from.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['route', 'from'],
      message: 'String must contain at least 1 character(s)',
    });
  }
  if (req.route.to.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['route', 'to'],
      message: 'String must contain at least 1 character(s)',
    });
  }
  for (let i = 0; i < req.items.length; i += 1) {
    const it = req.items[i];
    if (!it) continue;
    if (it.name.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'string',
        inclusive: true,
        path: ['items', i, 'name'],
        message: 'String must contain at least 1 character(s)',
      });
    }
    if (it.quantity < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['items', i, 'quantity'],
        message: 'Number must be greater than or equal to 1',
      });
    }
  }
};
